import _ from 'lodash'
import React, { useState, useEffect } from 'react'
import { PSClickWrap } from '@pactsafe/pactsafe-react-sdk'
import useSearchParams from '@vero/ui-core-browser/hooks/search-params.js'
import useRedirectParam from '@vero/ui-core-browser/hooks/redirect-param.js'
import useError from '@vero/ui-core-browser/hooks/error.js'
import useConfig from '@vero/ui-core-browser/hooks/config.js'
import useSubmit from '@vero/ui-core-browser/hooks/submit.js'
import Redirecting from '@vero/ui-components/components/redirecting.jsx'
import Input from '@vero/ui-components/components/input.jsx'
import Button from '@vero/ui-components/components/button.jsx'
import HeaderWithVero from '@vero/ui-components/components/header-with-vero.jsx'
import useClaims from '../hooks/claims.js'
import useEmail from '../hooks/email.js'
import useProviders from '../hooks/providers.js'
import EmailInput from '../components/email-input.jsx'
import PasswordInput from '../components/password-input.jsx'
import Providers from '../components/providers.jsx'
import SubmitButton from '../components/submit-button.jsx'

export default function ({ title, className }) {
  const { getSearchParamByKey } = useSearchParams()
  const temporaryPassword = getSearchParamByKey('temporary-password')
  const { redirect } = useRedirectParam()

  const [loading, setLoading] = useState(false)
  const { config } = useConfig()
  const { error, setError, handleError } = useError()
  const { email } = useEmail()
  const { authClient, storeTokens } = useClaims()
  const { providers, confirmed, hasEmailProvider, isSsoUser } = useProviders(email, setLoading, setError, handleError)
  const [password, setPassword] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [termsAgreed, setTermsAgreed] = useState(false)
  const [psReady, setPsReady] = useState(false)

  useEffect(() => {
    setPsReady(true)
  }, [])

  const resolvedCurrentPassword = temporaryPassword || currentPassword

  const onSubmit = useSubmit(setLoading, setError, handleError, async () => {
    if (!termsAgreed) {
      setError('You must agree to the terms and conditions')
    }

    if (!_.isEmpty(providers) && email && resolvedCurrentPassword && password && termsAgreed) {
      await storeTokens(await authClient.login(email, password, resolvedCurrentPassword), redirect)
    }
  })

  if (confirmed || isSsoUser()) {
    window.location.href = `/auth/login?email=${email}&r=${redirect}`
    return <Redirecting className={className} />
  }

  return (
    <div className={className}>
      {error && <div className='alert alert-error'>{error}</div>}

      <div className='p-8'>
        <HeaderWithVero title={title} />

        {email && (
          <form onSubmit={onSubmit} className='mb-6'>
            <div className='flex flex-col gap-4'>
              <EmailInput
                className='grow'
                value={email}
                readOnly
              />

              {hasEmailProvider() && !temporaryPassword && (
                <Input
                  type='password'
                  name='temporary-password'
                  label='temporary password'
                  className='grow'
                  autoComplete='off'
                  value={currentPassword}
                  onChange={e => {
                    setError(null)
                    setCurrentPassword(e.target.value)
                  }}
                  required
                />
              )}

              {hasEmailProvider() && resolvedCurrentPassword && (
                <PasswordInput
                  label='new password'
                  className='grow'
                  value={password}
                  onChange={e => {
                    setError(null)
                    setPassword(e.target.value)
                  }}
                  required
                />
              )}

              {psReady && config?.pactsafe?.accessId && !_.isEmpty(providers) && (
                <div id='pactsafe-container'>
                  <PSClickWrap
                    accessId={config?.pactsafe?.accessId}
                    groupKey={config?.pactsafe?.termsOfService?.groupKey}
                    signerId={email}
                    customData={{ email }}
                    allowDisagreed
                    onValid={() => {
                      setError(null)
                      setTermsAgreed(true)
                    }}
                    onInvalid={() => setTermsAgreed(false)}
                    onError={handleError}
                  />
                </div>
              )}

              <div className='flex flex-col gap-2'>
                {_.isEmpty(providers) && loading && <span className='loading loading-spinner self-center' />}

                {hasEmailProvider() && (
                  <>
                    <SubmitButton title={title} loading={loading} />

                    {!temporaryPassword && (
                      <Button
                        type='button'
                        className='btn-link uppercase'
                        onClick={() => {
                          if (!termsAgreed) {
                            setError('You must agree to the terms and conditions')
                          } else {
                            window.location.href = `/auth/reset-password?email=${email}&r=${redirect}`
                          }
                        }}
                      >
                        i dont have a temporary password
                      </Button>
                    )}
                  </>
                )}

                {isSsoUser() && (
                  <p className='alert alert-info'>Your user is a single sign-on user and does not need a password. Please use one of your providers below to sign in.</p>
                )}

                <Providers providers={providers} />

                {hasEmailProvider() && (
                  <span className='uppercase text-sm self-center'>
                    <span className='mr-1'>i have a password and want to</span>
                    <a
                      href={`/auth/login?email=${email}&r=${redirect}`}
                      className='link link-secondary'
                    >
                      sign in
                    </a>
                  </span>
                )}
              </div>
            </div>
          </form>
        )}
      </div>

      {!email && <div className='alert alert-error'>No email was specified in the URL</div>}
    </div>
  )
}
